/**
 * Textarea Component
 *
 * A textarea component with auto-resize, character count, and validation states.
 * Supports various sizes and styling variants.
 */

import React, { forwardRef, useState, useEffect, useCallback } from 'react';
import { cn } from '@utils/cn';

export type TextareaSize = 'sm' | 'md' | 'lg';
export type TextareaVariant = 'default' | 'filled' | 'outlined';
export type TextareaState = 'default' | 'error' | 'success' | 'warning';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  size?: TextareaSize;
  variant?: TextareaVariant;
  state?: TextareaState;
  label?: string;
  helperText?: string;
  errorText?: string;
  successText?: string;
  warningText?: string;
  showCount?: boolean;
  maxLength?: number;
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
  fullWidth?: boolean;
  required?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      size = 'md',
      variant = 'default',
      state = 'default',
      label,
      helperText,
      errorText,
      successText,
      warningText,
      showCount = false,
      maxLength,
      autoResize = false,
      minRows = 3,
      maxRows = 10,
      fullWidth = false,
      required = false,
      className,
      id,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState(value || '');
    const [rows, setRows] = useState(minRows);

    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = state === 'error' || !!errorText;
    const hasSuccess = state === 'success' || !!successText;
    const hasWarning = state === 'warning' || !!warningText;

    const baseClasses = cn(
      'block w-full rounded-lg border transition-colors duration-200 resize-none',
      'focus:outline-none focus:ring-1 focus:ring-offset-0',
      'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
      'dark:disabled:bg-gray-800 dark:disabled:text-gray-400',
      fullWidth && 'w-full'
    );

    const sizeClasses = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-3 py-2 text-base',
      lg: 'px-4 py-3 text-lg',
    };

    const variantClasses = {
      default: cn(
        'border-gray-200 bg-white text-gray-900',
        'focus:border-transparent focus:ring-0 focus:outline-none',
        'focus:input-gradient-border',
        'dark:border-gray-700 dark:bg-gray-800 dark:text-white',
        'dark:focus:border-transparent dark:focus:ring-0 dark:focus:outline-none',
        'dark:focus:input-gradient-border'
      ),
      filled: cn(
        'border-transparent bg-gray-100 text-gray-900',
        'focus:border-transparent focus:ring-0 focus:bg-white focus:outline-none',
        'focus:input-gradient-border',
        'dark:bg-gray-700 dark:text-white',
        'dark:focus:bg-gray-600 dark:focus:border-transparent dark:focus:ring-0 dark:focus:outline-none',
        'dark:focus:input-gradient-border'
      ),
      outlined: cn(
        'border-2 border-gray-200 bg-transparent text-gray-900',
        'focus:border-transparent focus:ring-0 focus:outline-none',
        'focus:input-gradient-border',
        'dark:border-gray-700 dark:text-white',
        'dark:focus:border-transparent dark:focus:ring-0 dark:focus:outline-none',
        'dark:focus:input-gradient-border'
      ),
    };

    const stateClasses = cn(
      hasError && 'border-red-200 dark:border-red-800',
      hasSuccess && 'border-green-200 dark:border-green-800',
      hasWarning && 'border-yellow-200 dark:border-yellow-800'
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange?.(e);
    };

    const calculateRows = useCallback(
      (text: string) => {
        const lineHeight = 20; // Approximate line height

        const textarea = document.createElement('textarea');
        textarea.style.cssText = `
        position: absolute;
        visibility: hidden;
        top: -9999px;
        left: -9999px;
        width: 100%;
        height: auto;
        font-size: inherit;
        font-family: inherit;
        line-height: inherit;
        padding: inherit;
        border: inherit;
        box-sizing: border-box;
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
      `;

        document.body.appendChild(textarea);
        textarea.value = text;
        const scrollHeight = textarea.scrollHeight;
        document.body.removeChild(textarea);

        const calculatedRows = Math.max(
          minRows,
          Math.min(maxRows, Math.ceil(scrollHeight / lineHeight))
        );
        return calculatedRows;
      },
      [minRows, maxRows]
    );

    // Sync internalValue with value prop when it changes externally
    useEffect(() => {
      if (value !== undefined && value !== internalValue) {
        setInternalValue(value as string);
      }
    }, [value, internalValue]);

    useEffect(() => {
      if (autoResize) {
        const newRows = calculateRows(internalValue as string);
        setRows(newRows);
      }
    }, [internalValue, autoResize, minRows, maxRows, calculateRows]);

    const getHelperText = () => {
      if (hasError && errorText) return errorText;
      if (hasSuccess && successText) return successText;
      if (hasWarning && warningText) return warningText;
      return helperText;
    };

    const getHelperTextColor = () => {
      if (hasError) return 'text-error-600 dark:text-error-400';
      if (hasSuccess) return 'text-success-600 dark:text-success-400';
      if (hasWarning) return 'text-warning-600 dark:text-warning-400';
      return 'text-gray-500 dark:text-gray-400';
    };

    const currentLength = (internalValue as string)?.length || 0;
    const isNearLimit = maxLength && currentLength >= maxLength * 0.9;
    const isOverLimit = maxLength && currentLength > maxLength;

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          value={internalValue}
          onChange={handleChange}
          rows={autoResize ? rows : minRows}
          maxLength={maxLength}
          className={cn(
            baseClasses,
            variantClasses[variant],
            sizeClasses[size],
            stateClasses,
            className
          )}
          aria-invalid={hasError}
          aria-describedby={
            hasError && errorText
              ? `${textareaId}-error`
              : helperText
                ? `${textareaId}-helper`
                : undefined
          }
          {...props}
        />

        {/* Character count */}
        {showCount && maxLength && (
          <div className="absolute bottom-2 right-2 text-xs text-gray-500 dark:text-gray-400">
            <span
              className={cn(
                isOverLimit && 'text-error-500',
                isNearLimit && !isOverLimit && 'text-warning-500'
              )}
            >
              {currentLength}/{maxLength}
            </span>
          </div>
        )}

        {/* Helper text and error messages */}
        {getHelperText() && (
          <p id={`${textareaId}-helper`} className={cn('mt-1 text-sm', getHelperTextColor())}>
            {getHelperText()}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
